import { z } from "zod";
import { type RoleType, VibrantRoles } from "@/models/provider/RoleDefinition";

export const DOSESPOT_PRACTITIONER_ROLE_TYPE = {
  PrescribingClinician: "prescribing-clinician",
  PrescribingAgentClinician: "prescribing-agent-clinician",
  ProxyClinician: "proxy-clinician",
} as const;

export const DOSESPOT_PRACTITIONER_ROLE_TYPE_VALUES = [
  DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingClinician,
  DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician,
  DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician,
] as const;

export const DosespotPractitionerRoleTypeSchema = z.enum(DOSESPOT_PRACTITIONER_ROLE_TYPE_VALUES);

export type DosespotPractitionerRoleType = z.infer<typeof DosespotPractitionerRoleTypeSchema>;

export const DOSESPOT_PRACTITIONER_ROLE_TYPE_LABELS: Record<DosespotPractitionerRoleType, string> =
  {
    [DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingClinician]: "Prescribing clinician",
    [DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician]: "Prescribing agent clinician",
    [DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician]: "Proxy clinician",
  };

export const DOSESPOT_PRACTITIONER_ROLE_TYPE_OPTIONS = DOSESPOT_PRACTITIONER_ROLE_TYPE_VALUES.map(
  (value) => ({
    value,
    label: DOSESPOT_PRACTITIONER_ROLE_TYPE_LABELS[value],
  }),
);

export const DOSESPOT_DERIVED_ROLE_COPY: Record<DosespotPractitionerRoleType, string> = {
  [DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingClinician]:
    "Prescriber - can prescribe directly with their own credentials.",
  [DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician]:
    "Delegated prescriber - can prescribe under a supervising prescriber.",
  [DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician]:
    "Prescription proxy - can prepare prescriptions under a supervising prescriber.",
};

export const DOSESPOT_ROLE_DERIVATION_OUTCOME = {
  Success: "success",
  MissingPracticeRole: "missing-practice-role",
  ContactRole: "contact-role",
  UnsupportedPracticeRole: "unsupported-practice-role",
} as const;

interface DosespotRoleDerivationSuccess {
  outcome: typeof DOSESPOT_ROLE_DERIVATION_OUTCOME.Success;
  roleType: DosespotPractitionerRoleType;
}

interface DosespotRoleDerivationFailure {
  outcome:
    | typeof DOSESPOT_ROLE_DERIVATION_OUTCOME.MissingPracticeRole
    | typeof DOSESPOT_ROLE_DERIVATION_OUTCOME.ContactRole
    | typeof DOSESPOT_ROLE_DERIVATION_OUTCOME.UnsupportedPracticeRole;
  message: string;
}

export type DosespotRoleDerivationResult =
  | DosespotRoleDerivationSuccess
  | DosespotRoleDerivationFailure;

const DOSESPOT_ROLE_BY_VIBRANT_ROLE: Partial<Record<RoleType, DosespotPractitionerRoleType>> = {
  [VibrantRoles.Admin]: DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician,
  [VibrantRoles.PrescribingProvider]: DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingClinician,
  [VibrantRoles.NonPrescribingProvider]: DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician,
};

const DOSESPOT_EPCS_NOT_APPLICABLE_ROLE_TYPES: readonly DosespotPractitionerRoleType[] = [
  DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician,
  DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician,
] as const;

const DOSESPOT_NPI_NOT_REQUIRED_ROLE_TYPES: readonly DosespotPractitionerRoleType[] = [
  DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician,
  DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician,
] as const;

// Prescription proxies (admins) only queue prescriptions for a supervising
// prescriber to submit — they hold no clinician license. Prescribing agents are
// licensed clinicians, so they still require one.
const DOSESPOT_LICENSE_NOT_REQUIRED_ROLE_TYPES: readonly DosespotPractitionerRoleType[] = [
  DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician,
] as const;

const DOSESPOT_SUPERVISING_CLINICIAN_REQUIRED_ROLE_TYPES: readonly DosespotPractitionerRoleType[] =
  [
    DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician,
    DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician,
  ] as const;

export const DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME = {
  Success: "success",
  MissingRole: "missing-role",
  UnsupportedRole: "unsupported-role",
} as const;

interface DosespotPractitionerRoleTypeMappingSuccess {
  outcome: typeof DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME.Success;
  roleType: DosespotPractitionerRoleType;
  practitionerRoleTypes: number[];
}

interface DosespotPractitionerRoleTypeMappingFailure {
  outcome:
    | typeof DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME.MissingRole
    | typeof DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME.UnsupportedRole;
  roleType?: string;
  message: string;
}

export type DosespotPractitionerRoleTypeMappingResult =
  | DosespotPractitionerRoleTypeMappingSuccess
  | DosespotPractitionerRoleTypeMappingFailure;

const DOSESPOT_PRACTITIONER_ROLE_TYPE_CODES: Record<DosespotPractitionerRoleType, number> = {
  [DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingClinician]: 1,
  [DOSESPOT_PRACTITIONER_ROLE_TYPE.PrescribingAgentClinician]: 5,
  [DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician]: 6,
};

/**
 * Derives the only valid DoseSpot practitioner role for a Vibrant practice role.
 */
export function deriveDosespotPractitionerRoleType(
  practiceRoleId: string | undefined,
): DosespotRoleDerivationResult {
  if (!practiceRoleId) {
    return {
      outcome: DOSESPOT_ROLE_DERIVATION_OUTCOME.MissingPracticeRole,
      message: "Provider must have an active practice role before DoseSpot setup can continue.",
    };
  }

  if (practiceRoleId === VibrantRoles.Contact) {
    return {
      outcome: DOSESPOT_ROLE_DERIVATION_OUTCOME.ContactRole,
      message: "Contacts are not clinical users and cannot use DoseSpot prescribing setup.",
    };
  }

  const parsedRoleId = z.nativeEnum(VibrantRoles).safeParse(practiceRoleId);
  if (!parsedRoleId.success) {
    return {
      outcome: DOSESPOT_ROLE_DERIVATION_OUTCOME.UnsupportedPracticeRole,
      message: `Unsupported practice role for DoseSpot setup: ${practiceRoleId}.`,
    };
  }

  const roleType = DOSESPOT_ROLE_BY_VIBRANT_ROLE[parsedRoleId.data];
  if (!roleType) {
    return {
      outcome: DOSESPOT_ROLE_DERIVATION_OUTCOME.UnsupportedPracticeRole,
      message: `Unsupported practice role for DoseSpot setup: ${practiceRoleId}.`,
    };
  }

  return {
    outcome: DOSESPOT_ROLE_DERIVATION_OUTCOME.Success,
    roleType,
  };
}

/**
 * Returns true when a practice role can enter the DoseSpot prescribing path.
 */
export function hasDerivedDosespotPractitionerRole(practiceRoleId: string | undefined): boolean {
  return (
    deriveDosespotPractitionerRoleType(practiceRoleId).outcome ===
    DOSESPOT_ROLE_DERIVATION_OUTCOME.Success
  );
}

/**
 * Returns the DoseSpot role implied by a Vibrant practice role.
 */
export function getDerivedDosespotRoleType(
  practiceRoleId: string | undefined,
): DosespotPractitionerRoleType | "" {
  const derivedRole = deriveDosespotPractitionerRoleType(practiceRoleId);
  return derivedRole.outcome === DOSESPOT_ROLE_DERIVATION_OUTCOME.Success
    ? derivedRole.roleType
    : "";
}

/**
 * Returns the operational copy for the DoseSpot role implied by a practice role.
 */
export function getDerivedDosespotRoleCopy(practiceRoleId: string | undefined): string | undefined {
  const roleType = getDerivedDosespotRoleType(practiceRoleId);
  return getDosespotPractitionerRoleTypeCopy(roleType);
}

/**
 * Returns the operational copy for a concrete DoseSpot role value.
 */
export function getDosespotPractitionerRoleTypeCopy(
  roleType: string | undefined,
): string | undefined {
  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return undefined;
  }

  return DOSESPOT_DERIVED_ROLE_COPY[parsedRoleType.data];
}

/**
 * Maps Vibrant's persisted clinician role to the numeric DoseSpot bot input.
 */
export function getDosespotPractitionerRoleTypes(
  roleType: string | undefined,
): DosespotPractitionerRoleTypeMappingResult {
  if (!roleType) {
    return {
      outcome: DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME.MissingRole,
      message: "DoseSpot practitioner role must be selected before enrollment can continue.",
    };
  }

  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return {
      outcome: DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME.UnsupportedRole,
      roleType,
      message: `Unsupported DoseSpot practitioner role: ${roleType}.`,
    };
  }

  return {
    outcome: DOSESPOT_PRACTITIONER_ROLE_TYPE_MAPPING_OUTCOME.Success,
    roleType: parsedRoleType.data,
    practitionerRoleTypes: [DOSESPOT_PRACTITIONER_ROLE_TYPE_CODES[parsedRoleType.data]],
  };
}

/**
 * Returns true when DoseSpot explicitly does not require clinician-owned EPCS/TFA.
 */
export function isDosespotEpcsNotApplicableForPractitionerRole(
  roleType: string | undefined,
): boolean {
  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return false;
  }

  return DOSESPOT_EPCS_NOT_APPLICABLE_ROLE_TYPES.includes(parsedRoleType.data);
}

/**
 * Returns true when DoseSpot requires a clinician-owned NPI for the selected role.
 */
export function isDosespotNpiRequiredForPractitionerRole(roleType: string | undefined): boolean {
  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return true;
  }

  return !DOSESPOT_NPI_NOT_REQUIRED_ROLE_TYPES.includes(parsedRoleType.data);
}

/**
 * Returns true when DoseSpot requires a state license for the selected role.
 * Prescription proxies queue prescriptions for a supervising prescriber and hold
 * no license; an unknown/unresolved role defaults to required (safe).
 */
export function isDosespotLicenseRequiredForPractitionerRole(
  roleType: string | undefined,
): boolean {
  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return true;
  }

  return !DOSESPOT_LICENSE_NOT_REQUIRED_ROLE_TYPES.includes(parsedRoleType.data);
}

/**
 * Returns true when the role can prescribe only under a supervising clinician.
 */
export function isDosespotSupervisingClinicianRequiredForPractitionerRole(
  roleType: string | undefined,
): boolean {
  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return false;
  }

  return DOSESPOT_SUPERVISING_CLINICIAN_REQUIRED_ROLE_TYPES.includes(parsedRoleType.data);
}

/**
 * Returns true when DoseSpot access for this role is gated by `dosespot-delegate-prescribing`.
 *
 * Only admin proxy clinicians are new behind that flag. Non-prescribing agents have used DoseSpot
 * under a supervising clinician for months and must keep working when the flag is off or missing.
 */
export function isDosespotDelegatePrescribingFeatureRole(roleType: string | undefined): boolean {
  const parsedRoleType = DosespotPractitionerRoleTypeSchema.safeParse(roleType);
  if (!parsedRoleType.success) {
    return false;
  }

  return parsedRoleType.data === DOSESPOT_PRACTITIONER_ROLE_TYPE.ProxyClinician;
}
